import { ComponentPropsWithoutRef, forwardRef } from "react";
import { Menu } from "../Menu";
import styles from "./PaginationOverflowMenu.module.scss";
import IconMoreHoriz from "@servicetitan/hammer-icon/mdi/round/more_horiz.svg";

/**
 * Props for the PaginationOverflowMenu component
 * @extends ComponentPropsWithoutRef<"li">
 */
export type PaginationOverflowMenuProps = ComponentPropsWithoutRef<"li"> & {
  /**
   * The starting page number for the overflow range.
   */
  startPage: number;
  /**
   * The ending page number for the overflow range.
   */
  endPage: number;
  /**
   * The number of pages in the overflow range.
   */
  length: number;
  /**
   * Callback function called when a page in the overflow menu is selected.
   * @param page The selected page number
   */
  onItemClick: (page: number) => void;
};

/**
 * PaginationOverflowMenu component for displaying overflow pages in pagination.
 *
 * Features:
 * - Displays ellipsis menu for large page ranges
 * - Shows all pages in the overflow range as menu items
 * - Integrates with pagination navigation system
 * - Fully accessible with proper ARIA labels
 * - Consistent styling with design system
 *
 * @example
 * <PaginationOverflowMenu
 *   startPage={4}
 *   endPage={8}
 *   length={5}
 *   onItemClick={(page) => console.log('Page selected:', page)}
 * />
 */
export const PaginationOverflowMenu = forwardRef<
  HTMLLIElement,
  PaginationOverflowMenuProps
>((props, ref) => {
  const { startPage, endPage, length, onItemClick, ...rest } = props;
  return (
    <li className={styles["pagination-item-overflow-menu"]} ref={ref} {...rest}>
      <Menu
        icon={IconMoreHoriz}
        size="small"
        appearance="ghost"
        maxHeight={200}
        key={`${startPage}-${endPage}`}
        contentClassName={styles["pagination-item-overflow-menu-content"]}
        aria-label="More pages"
      >
        {Array.from({ length: length }).map((_, index) => (
          <Menu.Item
            key={index}
            label={`${startPage + index}`}
            onClick={() => {
              onItemClick(startPage + index);
            }}
          />
        ))}
      </Menu>
    </li>
  );
});

PaginationOverflowMenu.displayName = "PaginationOverflowMenu";
